import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Grid,
  DialogActions,
  Box,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import { MODES } from '../../lib/constants'
import { CustomDialogTitle } from './CustomerStyledComponents'
import './ModalStyle.scss'
import { useQuery, useMutation } from '@apollo/client'
import { gqlTransporter, gqlUsers, gqlToken } from '../../gql'
import dayjs from 'dayjs'

// Helper function to retrieve the result message
const getResultMessage = (t, mode, result) => t(`${result}.${mode}`)

function OccasionalDriverModal({ open, onClose, mode, refresh, selectedDate }) {
  const { t } = useTranslation('occasional_driver')
  const [isSuccessSaved, setIsSuccessSaved] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [isErrorSave, setIsErrorSave] = useState(false)
  const [errorSaveMessage, setErrorSaveMessage] = useState('')
  const [selectedTransporterId, setSelectedTransporterId] = useState('')

  // Queries
  const { data: sites, loading: loadingSites, error: errorSites } = useQuery(gqlUsers.SITES)
  const { data: roles, loading: loadingRoles, error: errorRoles } = useQuery(gqlUsers.ROLES)
  const {
    data: transporterData,
    loading: loadingTransporter,
    error: errorTransporter,
  } = useQuery(gqlTransporter.TRANSPORTERS)

  // Mutation
  const [createUser] = useMutation(gqlUsers.CREATE_USER)
  const [createOneTimeToken] = useMutation(gqlToken.CREATE_TOKEN)

  // Available transporters
  const availableTransporters =
    transporterData?.transporters.edges.map(({ node }) => ({
      id: node.id,
      name: node.name,
    })) || []

  // Combined loading and error checks
  const loading = loadingTransporter || loadingSites || loadingRoles
  const error = errorTransporter || errorSites || errorRoles

  // Handle saving the driver
  const handleSave = async () => {
    const driverOccasionalRoleId = roles?.roles.nodes.find(
      (role) => role.name === 'bdn_driver_occasional'
    )?.id
    const selectedTransporter = availableTransporters.find(
      (transporter) => transporter.id === selectedTransporterId
    )
    try {
      if (mode === MODES.CREATE) {
        const { data: token } = await createOneTimeToken({
          variables: {
            deliveryWorkDate: dayjs(selectedDate).format('YYYY-MM-DD'),
            transporterId: selectedTransporterId
          },
        })
        await createUser({
          variables: {
            email: selectedTransporter.name,
            firstname: selectedTransporter.name,
            lastname: selectedTransporter.name,
            idRole: driverOccasionalRoleId,
            idSite: sites?.sites.nodes[0]?.id,
            transporterId: selectedTransporterId,
            deliveryWorkDate: dayjs(selectedDate).format('YYYY-MM-DD'),
            tokenId: token.createOneTimeToken.oneTimeToken.token
          },
        })
        
      }

      // Success handling
      setSuccessMessage(getResultMessage(t, mode, 'success'))
      setIsSuccessSaved(true)
      setTimeout(() => {
        setIsSuccessSaved(false)
        refresh()
        onClose()
      }, 2000)
    } catch (err) {
      console.error('ApolloError:', err)
      setErrorSaveMessage(getResultMessage(t, mode, 'error'))
      setIsErrorSave(true)
      setTimeout(() => {
        setIsErrorSave(false)
        refresh()
        onClose()
      }, 2000)
    }
  }

  // Render loading or error states
  const renderLoadingOrError = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress size={80} />
        </Box>
      )
    }

    if (error) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Typography color="error">{t('errorLoadingData')}</Typography>
        </Box>
      )
    }

    return null
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <CustomDialogTitle>
        <div>
          <h5>{mode === MODES.CREATE ? t('addNewOccasionalDriver') : t('editOccasionalDriver')}</h5>
        </div>
        <Button style={{ color: 'inherit', fontSize: '1rem' }} onClick={onClose}>
          X
        </Button>
      </CustomDialogTitle>
      {isSuccessSaved && <Box className="success-message">{successMessage}</Box>}
      {isErrorSave && <Box className="error-message">{errorSaveMessage}</Box>}

      {renderLoadingOrError() || (
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">{t('chooseTransporter')}</Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedTransporterId}
                  onChange={(e) => setSelectedTransporterId(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">{t('transporters')}</MenuItem>
                  {availableTransporters.map((transporter) => (
                    <MenuItem key={transporter.id} value={transporter.id}>
                      {transporter.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
      )}

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="warning">
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={!selectedTransporterId}
          variant="contained"
          color="primary"
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

OccasionalDriverModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  selectedDate: PropTypes.any.isRequired
}

export default OccasionalDriverModal